import {useEffect, useRef, useState} from "react";
import {Alert, Card} from "react-bootstrap";
import {Link, useSearchParams} from "react-router";
import {ProfileApi} from "../api/ProfileApi";
import {useAuth} from "../auth/AuthContext";
import {ErrorAlert} from "../components/ErrorAlert";
import {LoadingSpinner} from "../components/LoadingSpinner";

// Opened from the "confirm your new email address" email: /verify-email?token=…
export function VerifyEmailPage() {
    const [params] = useSearchParams();
    const token = params.get('token') ?? '';
    const {user, renew} = useAuth();
    const [state, setState] = useState<'working' | 'done' | 'failed'>(token ? 'working' : 'failed');
    const [error, setError] = useState<unknown>();
    const started = useRef(false);

    useEffect(() => {
        // Once only (StrictMode runs effects twice): each verification also emails the account.
        if (!token || started.current) {
            return;
        }
        started.current = true;
        ProfileApi.verifyEmail(token).then(
            async () => {
                setState('done');
                // The session's copy of the email address is in the access token: renew it.
                if (user) {
                    await renew().catch(() => undefined);
                }
            },
            err => {
                setError(err);
                setState('failed');
            });
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Card className="auth-card mx-auto mt-md-4">
            <Card.Body className="p-4">
                <h1 className="h4 mb-3">Confirm email address</h1>
                {state === 'working' && <LoadingSpinner/>}
                {state === 'done' && (
                    <Alert variant="success">
                        Your email address has been changed. {user ? <Link to="/account/profile">Back to your profile</Link> : <Link to="/login">Sign in</Link>}.
                    </Alert>
                )}
                {state === 'failed' && (error
                    ? <ErrorAlert error={error} messages={{ 404: 'This link has expired or is invalid. Change the address again from your profile to get a new one.' }}/>
                    : <Alert variant="warning">This page needs the link from the email.</Alert>)}
            </Card.Body>
        </Card>
    );
}
